import { useState } from "react";
import { useIntl } from "react-intl";
import { SiloStepModalForm, SimpleModal } from "src/app/components";
import { IActionsModal } from "src/app/types/actionsModal";
import { SiloStepsList } from "./SiloStepsList";
import { toast } from "react-toastify";
import { OrderTransport } from "src/app/transports/order";
import { useGetOrderById, useSemRushKeywords } from "src/app/hooks";
import { UpsertNumberOfTexts } from "src/app/types/BoostSilo";
import { LangType } from "src/app/types/LangType";
import { UpsertNumberOfTextsSchema } from "src/app/types/BoostSilo";
import { getKeywordFromString } from "src/app/utils/keywordsHelper";
import { Button } from "react-bootstrap";

export interface TEntity {
    id?: number;
    keywords?: string[];
    orderId?: number;
    keyword?: string;
}

const SiloStepModalFormContainer = (props: IActionsModal<TEntity>) => {
    const { handleClose = () => {}, entity, updateGridOnSave } = props;

    const orderId = entity.orderId ? entity.orderId : entity.id;

    const { formatMessage } = useIntl();

    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedKeywords, setSelectedKeywords] = useState<Array<string>>([]);
    const [data, setData] = useState<UpsertNumberOfTexts>();

    const { order, loading: loadingOrder } = useGetOrderById(orderId);

    const currentKeyword =
        entity.keywords && entity.keywords.length > 0 ? entity.keywords[0] : entity.keyword;

    const {
        keywords: semRushKeywords,
        loading: keywordLoading,
        refresh,
        error,
    } = useSemRushKeywords();

    const handleSelectedKeyword = (keyword: string) => {
        if (keyword === "REMOVE_KEYWORDS") {
            setSelectedKeywords([]);
            return;
        }
        if (keyword === "ALL_KEYWORDS") {
            setSelectedKeywords(semRushKeywords?.map((el) => el.keyword || ""));
            return;
        }

        setSelectedKeywords((values) => {
            const index = values.indexOf(keyword);

            if (index > -1) {
                values.splice(index, 1);
                return [...values];
            } else {
                return [...values, keyword];
            }
        });
    };

    const stepsList = SiloStepsList(
        selectedKeywords,
        handleSelectedKeyword,
        semRushKeywords,
        keywordLoading,
        error
    );

    const goToStep = (step: number, _values: any) => {
        if (!order || loadingOrder) return;
        if (step === 1 && currentKeyword && data?.numberOfTexts !== _values?.numberOfTexts) {
            const lang = order.moneySite?.lang;
            setData({ numberOfTexts: _values.numberOfTexts });
            refresh(currentKeyword, _values?.numberOfTexts, lang as LangType);
        }
        if (step === 3 && _values.userKeywords) {
            setSelectedKeywords((val) => [...val, ...getKeywordFromString(_values.userKeywords)]);
        }

        setCurrentStep(step);
    };

    const confirmationLabel = () => {
        return currentStep === (stepsList.length || 1) - 1
            ? formatMessage({ id: "common.action.save" })
            : formatMessage({ id: "common.action.continue" });
    };

    const handleSubmit = async () => {
        if (!data || !order) return;
        setLoading(true);

        try {
            setLoading(true);
            await OrderTransport.createBoostSiloOrders(order.id, selectedKeywords);
            toast.success(formatMessage({ id: "common.notification.success" }));
            updateGridOnSave && updateGridOnSave({}, true);
            handleClose();
        } catch (err: any) {
            toast.error(formatMessage({ id: "common.notification.error" }));
        } finally {
            setLoading(false);
        }
    };

    const FooterLeftComponent = (
        <div className="w-100 d-flex justify-content-end">
            {currentStep !== 0 && (
                <Button onClick={() => setCurrentStep((step) => (step - 1 === 0 ? 0 : step - 1))}>
                    {formatMessage({ id: "common.action.return" })}
                </Button>
            )}
        </div>
    );

    return (
        <SimpleModal
            entityName={`${formatMessage({ id: "common.entity.silo" })}: ${currentKeyword || ""}`}
            formId={`stepperForm${currentStep}`}
            loading={loading || loadingOrder}
            buttonConfirmationLabel={confirmationLabel()}
            handleClose={() => {
                goToStep(0, {});
                handleClose();
            }}
            showModal={true}
            modalProps={{ size: "xl" }}
            FooterLeftComponent={FooterLeftComponent}
        >
            <SiloStepModalForm
                initialValues={UpsertNumberOfTextsSchema.getDefault()}
                currentStep={currentStep}
                onSubmit={handleSubmit}
                maxSteps={stepsList.length}
                stepsList={stepsList}
                goToStep={goToStep}
            />
        </SimpleModal>
    );
};

export default SiloStepModalFormContainer;
